import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, useTheme } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { useState } from "react";

import TemplateIcon from "@/components/Icons/TemplateIcon";
import ConfirmDiscardChanges from "@/components/ModalForms/ConfirmDiscardChanges.tsx";
import { WsTemplateExtended } from "@/components/Templates/templates.ts";
import useTemplates from "@/components/Templates/useTemplates.ts";
export default function ListTemplates({
  templates,
  setActiveTemplate,
  activeId,
  additionalButtons,
  showAuthor,
}: {
  templates: WsTemplateExtended[];
  setActiveTemplate: (value: WsTemplateExtended) => void;
  activeId: string | null;
  additionalButtons: boolean;
  showAuthor: boolean;
}) {
  const iconSize = "16px";
  const theme = useTheme();
  const { cloneTemplate, deleteTemplate } = useTemplates();
  const [confirmDelete, setConfirmDelete] = useState<WsTemplateExtended | null>(null);

  const cloneAndSetTemplate = async (template: WsTemplateExtended) => {
    if (!template?.id) {
      console.error("No template to clone");
      return;
    }
    const clone = await cloneTemplate(template.id);
    if (clone?.id) {
      setActiveTemplate(clone);
    }
  };

  const confirmAndDelete = async () => {
    if (!confirmDelete) {
      console.error("No template to delete");
      return;
    }
    try {
      await deleteTemplate(confirmDelete.id);
      setConfirmDelete(null);
    } catch (e) {
      console.error({ e });
    }
  };

  return (
    <>
      <ConfirmDiscardChanges
        id={`confirm-delete-template-${confirmDelete?.id}`}
        visible={!!confirmDelete?.id}
        label="Are you sure?"
        text="Confirm you would like to delete this template. Your content will be lost."
        deleteText="Delete"
        denyCloseHandler={() => setConfirmDelete(null)}
        confirmCloseHandler={confirmAndDelete}
      />
      {templates?.map((template, index) => (
        <div key={`list-templates-${template.id}-${index}`}>
          {additionalButtons ? (
            <Box
              component="button"
              className="basic-button"
              sx={{
                display: "block",
                width: "100%",
                textAlign: "left",
                px: 1.5,
                py: 1,
                mb: index !== templates.length - 1 ? 0.5 : 0,
                background: activeId === template?.id ? theme.palette.secondary.main : theme.palette.secondary.dark,
                "&:hover": {
                  background: activeId === template.id ? theme.palette.secondary.main : "",
                },
              }}
              onClick={() => setActiveTemplate(template)}
            >
              {template.name}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                px: 1.5,
                py: 1,
                alignItems: "center",
                mb: index !== templates.length - 1 ? 0.5 : 0,
              }}
            >
              <Box sx={{ width: "70%" }}>
                <Tooltip title={template?.canEdit ? "Edit Template" : "View Template"}>
                  <Box
                    component="button"
                    className="basic-button"
                    sx={{ width: "100%", p: 1, justifyContent: "start", textAlign: "left" }}
                    onClick={() => setActiveTemplate(template)}
                  >
                    {template.name}
                  </Box>
                </Tooltip>
              </Box>

              <Box sx={{ width: "30%", justifyContent: "end", alignItems: "center", display: "flex", gap: 2 }}>
                {showAuthor && (
                  <Box
                    sx={{
                      fontSize: "14px",
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      wordBreak: "break-word",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Tooltip title={`${template.authorName || "Unknown"}`}>
                      <span>{template.authorName || "Unknown"}</span>
                    </Tooltip>
                  </Box>
                )}
                <Tooltip title="Clone Template">
                  <IconButton onClick={() => cloneAndSetTemplate(template)}>
                    <ContentCopyIcon sx={{ fontSize: iconSize }} />
                  </IconButton>
                </Tooltip>
                {template?.canEdit && (
                  <Tooltip title="Delete Template">
                    <IconButton onClick={() => setConfirmDelete(template)} sx={{ width: 44 }}>
                      <DeleteIcon sx={{ fontSize: iconSize }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          )}
        </div>
      ))}
    </>
  );
}
